import { getCurrencyMinorUnitsSafe } from "@redotech/money/currencies";
import { REDO_CART_STORAGE_KEY } from "@redotech/redo-storefront-web/cache/keys";
import { setSessionStorageWithExpiry } from "@redotech/redo-web/utils/session-storage-wrapper";
import {
  CoverageFeeCapability,
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { CurrencyCode } from "@redotech/shopify-client/storefront.graphql";
import { retry } from "@redotech/util/retry";
import { log } from "../monitoring/log";
import {
  isBigcommerceCheckout,
  refreshCheckout,
} from "./bigcommerce-checkout-refresh";
import { BigcommerceGraphQLClient } from "./bigcommerce-graphql";
import { BigcommerceServerApiClient } from "./bigcommerce-server-api-client";

/**
 * @see https://developer.bigcommerce.com/docs/graphql-storefront
 * @see https://developer.bigcommerce.com/graphql-storefront/reference
 */
interface Checkout {
  entityId: string;
  subtotal: { value: number; currencyCode: string };
  grandTotal: { value: number; currencyCode: string };
  taxTotal: { value: number; currencyCode: string };
  shippingCostTotal: { value: number; currencyCode: string };
}

export interface BigcommerceCartSelectedOption {
  entityId: number;
  name: string;
  __typename: string;
  value?: string;
  valueEntityId?: number;
  number?: number;
  text?: string;
  date?: { utc: string };
  fileName?: string;
}

export interface BigcommerceCartLineProductOption {
  optionId: string;
  value: string;
  displayName?: string;
  displayValue?: string;
  optionType?: string;
}

/**
 * Flatten a cart line's selectedOptions union. List types expose
 * `valueEntityId` for pricing replay; value types carry a display value only.
 */
export function normalizeBigcommerceSelectedOptions(
  selectedOptions: BigcommerceCartSelectedOption[] | undefined,
): BigcommerceCartLineProductOption[] | undefined {
  if (!selectedOptions?.length) {
    return undefined;
  }
  return selectedOptions.map((option) => {
    const displayValue =
      option.valueEntityId != null
        ? option.value
        : (option.text ??
          option.fileName ??
          (option.number != null ? String(option.number) : undefined) ??
          option.date?.utc ??
          option.value);
    return {
      optionId: String(option.entityId),
      value:
        option.valueEntityId != null
          ? String(option.valueEntityId)
          : (displayValue ?? ""),
      displayName: option.name,
      displayValue,
      optionType: option.__typename,
    };
  });
}

/**
 * The pricing fields we read off a BigCommerce cart line. Gift certificates
 * carry only an `amount`, so callers map those onto `extendedSalePrice`.
 */
export interface BigcommerceCartLinePricing {
  quantity?: number;
  listPrice?: { value?: number } | null;
  salePrice?: { value?: number } | null;
  extendedListPrice?: { value?: number } | null;
  extendedSalePrice?: { value?: number } | null;
  couponAmount?: { value?: number } | null;
}

/**
 * BigCommerce line prices exclude coupons, and each line's `couponAmount` is
 * its already-allocated share, so subtract it to get the post-discount total.
 */
export function normalizeBigcommerceLinePrices({
  lines,
  currencyCode,
}: {
  lines: BigcommerceCartLinePricing[];
  currencyCode?: string | null;
}): { linePrice: number; originalLinePrice: number }[] {
  // Round in the currency's minor units; `line_price` is major units x100.
  const minorPerMajor = 10 ** getCurrencyMinorUnitsSafe(currencyCode ?? "");
  const toMinor = (value: number | null | undefined): number | undefined =>
    typeof value === "number" && Number.isFinite(value)
      ? Math.round(value * minorPerMajor)
      : undefined;
  const toLinePrice = (minor: number) => (minor * 100) / minorPerMajor;

  return lines.map((line) => {
    const saleMinor =
      toMinor(line.extendedSalePrice?.value) ??
      toMinor((line.salePrice?.value ?? 0) * (line.quantity ?? 0)) ??
      0;
    const preDiscountMinor =
      toMinor(line.extendedListPrice?.value) ??
      (toMinor((line.listPrice?.value ?? 0) * (line.quantity ?? 0)) ||
        saleMinor);
    const couponMinor = toMinor(line.couponAmount?.value) ?? 0;
    return {
      linePrice: toLinePrice(Math.max(0, saleMinor - couponMinor)),
      originalLinePrice: toLinePrice(Math.max(saleMinor, preDiscountMinor)),
    };
  });
}

export class BigcommerceStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.BIGCOMMERCE;
  graphQLClient: BigcommerceGraphQLClient;
  auth: string;
  storeUrl: string;
  cartEntityId?: string;
  private readonly widgetSlug?: string;

  constructor({
    auth,
    storeUrl,
    cartEntityId,
    widgetSlug,
  }: {
    auth: string;
    storeUrl: string;
    cartEntityId?: string;
    widgetSlug?: string;
  }) {
    this.auth = auth;
    this.storeUrl = storeUrl;
    this.cartEntityId = cartEntityId;
    this.widgetSlug = widgetSlug;
    this.graphQLClient = new BigcommerceGraphQLClient(auth);
  }

  /** Custom fee attach/remove for merchants selling coverage as a checkout fee. */
  coverageFee: CoverageFeeCapability = {
    isAvailable: async (): Promise<boolean> =>
      !!globalThis.location?.pathname?.includes("/checkout") &&
      !!this.cartEntityId,

    attach: async (input): Promise<{ changed: boolean }> => {
      const widgetSlug = input.widgetSlug ?? this.widgetSlug;
      if (!widgetSlug || !this.cartEntityId) {
        return { changed: false };
      }
      const response =
        await new BigcommerceServerApiClient().attachCustomFeeToCart({
          widgetId: widgetSlug,
          checkoutId: this.cartEntityId,
          feeAmount: input.feeAmount,
          productType: input.productType,
        });
      return { changed: !!response.feeUpdated };
    },

    remove: async (input): Promise<{ changed: boolean }> => {
      const widgetSlug = input?.widgetSlug ?? this.widgetSlug;
      if (!widgetSlug || !this.cartEntityId) {
        return { changed: false };
      }
      const response =
        await new BigcommerceServerApiClient().deleteCustomFeeFromCart({
          widgetId: widgetSlug,
          checkoutId: this.cartEntityId,
        });
      return { changed: !!response.feeUpdated };
    },
  };

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    if (data.items.length !== 1) {
      return;
    }

    const query = `
      mutation addCartLineItems($addCartLineItemsInput: AddCartLineItemsInput!) {
        cart {
          addCartLineItems(input: $addCartLineItemsInput) {
            cart {
              entityId
            }
          }
        }
      }
    `;

    await this.graphQLClient.query({
      query,
      variables: {
        addCartLineItemsInput: {
          cartEntityId: this.cartEntityId,
          data: {
            lineItems: data.items.map((item) => ({
              productEntityId: item.product_id,
              variantEntityId: item.variant_id || item.id, // FIXME: Canonically, a ShopifyCartItem.id refers to the line item ID not a variant ID and they are not convertible. The rest of manager uses id as a variant id though, so leaving for now.
              quantity: item.quantity,
            })),
          },
        },
      },
    });
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    // throw new Error("Bigcommerce Unsupported");

    const query = `
      query getCart {
        site {
          cart {
            entityId
            currencyCode
            metafields(first: 50, namespace: "bc_storefront") {
              edges {
                node {
                  entityId
                  key
                  value
                }
              }
            }
            lineItems {
              physicalItems {
                entityId
                parentEntityId
                variantEntityId
                productEntityId
                name
                brand
                sku
                url
                quantity
                listPrice {
                  currencyCode
                  value
                }
                originalPrice {
                  currencyCode
                  value
                }
                salePrice {
                  currencyCode
                  value
                }
                extendedListPrice {
                  currencyCode
                  value
                }
                extendedSalePrice {
                  currencyCode
                  value
                }
                couponAmount {
                  currencyCode
                  value
                }
                isShippingRequired
                selectedOptions {
                  entityId
                  name
                  __typename
                  ... on CartSelectedMultipleChoiceOption {
                    value
                    valueEntityId
                  }
                  ... on CartSelectedCheckboxOption {
                    value
                    valueEntityId
                  }
                  ... on CartSelectedNumberFieldOption {
                    number
                  }
                  ... on CartSelectedMultiLineTextFieldOption {
                    text
                  }
                  ... on CartSelectedTextFieldOption {
                    text
                  }
                  ... on CartSelectedDateFieldOption {
                    date {
                      utc
                    }
                  }
                  ... on CartSelectedFileUploadOption {
                    fileName
                  }
                }
              }
              digitalItems {
                entityId
                parentEntityId
                variantEntityId
                productEntityId
                name
                brand
                sku
                url
                quantity
                listPrice {
                  currencyCode
                  value
                }
                originalPrice {
                  currencyCode
                  value
                }
                salePrice {
                  currencyCode
                  value
                }
                extendedListPrice {
                  currencyCode
                  value
                }
                extendedSalePrice {
                  currencyCode
                  value
                }
                couponAmount {
                  currencyCode
                  value
                }
                selectedOptions {
                  entityId
                  name
                  __typename
                  ... on CartSelectedMultipleChoiceOption {
                    value
                    valueEntityId
                  }
                  ... on CartSelectedCheckboxOption {
                    value
                    valueEntityId
                  }
                  ... on CartSelectedNumberFieldOption {
                    number
                  }
                  ... on CartSelectedMultiLineTextFieldOption {
                    text
                  }
                  ... on CartSelectedTextFieldOption {
                    text
                  }
                  ... on CartSelectedDateFieldOption {
                    date {
                      utc
                    }
                  }
                  ... on CartSelectedFileUploadOption {
                    fileName
                  }
                }
              }
              giftCertificates {
                entityId
                name
                amount {
                  currencyCode
                  value
                }
                isTaxable
              }
            }
            amount {
              currencyCode
              value
            }
          }
          checkout {
            grandTotal {
              currencyCode
              value
            }
          }
        }
      }
    `;

    const json = await this.graphQLClient.query({ query });
    const cartData = json.data?.site.cart;

    if (cartData?.entityId) {
      this.cartEntityId = cartData.entityId;
    }

    const cartItems: any[] = []; // TODO: type
    // Kept in lockstep with cartItems so the prorated prices land on the right line.
    const linePricing: BigcommerceCartLinePricing[] = [];

    if (cartData?.lineItems?.physicalItems) {
      for (const item of cartData.lineItems.physicalItems) {
        cartItems.push({
          id: item.variantEntityId, // Technically not accurate implementation because should be line item ID but matches remaining manager implementation
          line_id: item.entityId,
          quantity: item.quantity,
          product_id: item.productEntityId,
          variant_id: item.variantEntityId,
          handle: item.productEntityId,
          title: item.name,
          vendor: item.brand,
          requires_shipping: item.isShippingRequired || true,
          sku: item.sku,
          productOptions: normalizeBigcommerceSelectedOptions(
            item.selectedOptions,
          ),
        });
        linePricing.push(item);
      }
    }
    if (cartData?.lineItems?.digitalItems) {
      for (const item of cartData.lineItems.digitalItems) {
        cartItems.push({
          id: item.variantEntityId,
          line_id: item.entityId,
          quantity: item.quantity,
          product_id: item.productEntityId,
          variant_id: item.variantEntityId,
          handle: item.productEntityId,
          title: item.name,
          vendor: item.brand,
          requires_shipping: false,
          sku: item.sku,
          productOptions: normalizeBigcommerceSelectedOptions(
            item.selectedOptions,
          ),
        });
        linePricing.push(item);
      }
    }
    if (cartData?.lineItems?.giftCertificates) {
      for (const item of cartData.lineItems.giftCertificates) {
        cartItems.push({
          id: item.entityId,
          line_id: item.entityId,
          product_id: `gift_card_${item.entityId}`,
          variant_id: `gift_card_${item.entityId}`,
          title: item.name,
          vendor: item.name, // This field doesn't exist in Bigcommerce
          requires_shipping: false,
        });
        linePricing.push({
          quantity: 1,
          extendedSalePrice: item.amount,
          extendedListPrice: item.amount,
        });
      }
    }

    const normalizedPrices = normalizeBigcommerceLinePrices({
      lines: linePricing,
      currencyCode: cartData?.currencyCode,
    });
    for (const [
      i,
      { linePrice, originalLinePrice },
    ] of normalizedPrices.entries()) {
      cartItems[i].line_price = linePrice;
      cartItems[i].original_line_price = originalLinePrice;
    }

    const attributes: Record<string, string> = {};
    if (cartData?.metafields?.edges) {
      for (const edge of cartData.metafields.edges) {
        attributes[edge.node.key] = edge.node.value;
      }
    }

    const returnVal: ShopifyCart = {
      attributes,
      items: cartItems,
      currency: cartData?.currencyCode ?? CurrencyCode.Usd,
      total_price:
        json.data?.site?.checkout?.grandTotal?.value ?? cartData?.amount?.value,
    };

    return returnVal;
  }

  async cartChange(
    change: ShopifyCartChange & { productId?: string | number },
    signal?: AbortSignal,
    preventRefresh = false,
  ): Promise<void> {
    if (!this.cartEntityId) {
      await this.cartGet();
    }

    const query = change.quantity
      ? `
      mutation CartLineUpdate($line: UpdateCartLineItemInput!) {
        cart {
          updateCartLineItem(input: $line) {
            cart {
              entityId
            }
          }
        }
      }
    `
      : `
      mutation CartLineRemove($line: DeleteCartLineItemInput!) {
        cart {
          deleteCartLineItem(input: $line) {
            cart {
              entityId
            }
            deletedLineItemEntityId
            deletedCartEntityId
          }
        }
      }
    `;

    await retry(
      async () => {
        const json = await this.graphQLClient.query({
          query,
          variables: {
            line: {
              cartEntityId: this.cartEntityId,
              lineItemEntityId: change.line,
              ...(change.quantity
                ? {
                    data: {
                      lineItem: {
                        quantity: change.quantity,
                        productEntityId: Number(change.productId),
                        variantEntityId: Number(change.id),
                      },
                    },
                  }
                : {}),
            },
          },
        });
        if (
          change.quantity &&
          (json.errors?.length || !json.data?.cart?.updateCartLineItem?.cart)
        ) {
          throw new Error(
            `BigCommerce quantity update failed: ${JSON.stringify(json.errors ?? [])}`,
          );
        }
      },
      change.quantity ? 2 : 1,
    );

    setSessionStorageWithExpiry(REDO_CART_STORAGE_KEY, undefined, 0.15);
    if (!preventRefresh) {
      await this.refreshCheckout();
    }
  }

  async refreshCheckout(): Promise<void> {
    setSessionStorageWithExpiry(REDO_CART_STORAGE_KEY, undefined, 0.15);
    if (
      !isBigcommerceCheckout() ||
      !(await refreshCheckout().catch(() => false))
    ) {
      location.reload();
    }
  }

  async updateCartAttribute({
    attributes,
  }: {
    attributes: { key: string; value: string }[];
  }): Promise<ShopifyCart> {
    // First get existing metafields
    const getQuery = `
      query getCartMetafields {
        site {
          cart {
            metafields(first: 50, namespace: "bc_storefront") {
              edges {
                node {
                  entityId
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;

    const response = await this.graphQLClient.query({ query: getQuery });
    if (!response?.data?.site?.cart) {
      log(
        "BigcommerceStorefrontClient: no cart found, skipping metafield update",
      );
      return this.cartGet();
    }
    const existingMetafields = response.data.site.cart.metafields.edges.map(
      (edge: any) => edge.node,
    );

    // Prepare mutations based on comparison
    const mutations = attributes
      .map((attr, index) => {
        const existingMetafield = existingMetafields.find(
          (m: any) => m.key === attr.key,
        );

        if (!attr.value) {
          // Delete if exists and new value is empty
          return existingMetafield
            ? `
          deleteMetafield${index}: deleteCartMetafield(input: {
            cartEntityId: "${this.cartEntityId}",
            metafieldEntityId: ${existingMetafield.entityId}
          }) {
            deletedMetafieldEntityId
          }
        `
            : null;
        }

        if (existingMetafield) {
          // Update existing
          return `
          updateMetafield${index}: updateCartMetafield(input: {
            cartEntityId: "${this.cartEntityId}",
            metafieldEntityId: ${existingMetafield.entityId},
            data: {
              key: "${attr.key}",
              value: "${attr.value}"
            }
          }) {
            metafield {
              entityId
              key
              value
            }
          }
        `;
        }

        // Create new
        return `
        createMetafield${index}: createCartMetafield(input: {
          cartEntityId: "${this.cartEntityId}",
          data: {
            key: "${attr.key}",
            value: "${attr.value}"
          }
        }) {
          metafield {
            entityId
            key
            value
          }
        }
        `;
      })
      .filter(Boolean); // Remove null mutations

    if (mutations.length === 0) {
      return this.cartGet();
    }

    const mutation = `
      mutation updateCartMetafields {
        cart {
          ${mutations.join("\n")}
          }
        }
        `;

    await this.graphQLClient.query({ query: mutation });
    return this.cartGet();
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
    preventRefresh = false,
  ): Promise<undefined> {
    const newQuantity = item.quantity - numToRemove;

    await this.cartChange(
      {
        id: String(item.variant_id || item.id),
        productId: item.product_id,
        quantity: newQuantity,
        line: item.line_id,
      },
      signal,
      preventRefresh,
    );
  }

  async updateCartItemSellingPlan(): Promise<undefined> {}

  async cartClear(): Promise<void> {}

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    if (!handle) {
      console.warn("Bigcommerce product handle is missing.");
      return undefined;
    }

    const query = `
        query getProductById($productId: Int!) {
          site {
            product(entityId: $productId) {
              id
              entityId
              type
              categories {
                edges {
                  node {
                    entityId
                    name
                  }
                }
              }
              variants {
                edges {
                  node {
                    entityId
                    prices {
                      price {
                        value
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `;

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return json.data.site.product.categories.edges.map((categoryEdge: any) => ({
      title: categoryEdge.node.name,
      handle: String(categoryEdge.node.entityId),
    }));
  }

  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    if (!this.cartEntityId) {
      await this.cartGet();
    }

    const query = `
      query getProductById($productId: Int!) {
        site {
            product(entityId: $productId) {
              id
              entityId
              type
            variants {
              edges {
                node {
                    entityId
                  prices {
                    price {
                        value
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `;

    // TODO: may need to handle currency, unclear what's presentment vs transaction vs shop - ignoring for MVP, assume its transaction

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return {
      id: json.data.site.product.entityId,
      tags: "",
      title: json.data.site.product.name,
      variants: json.data.site.product.variants.edges.map(
        (variantEdge: any) => ({
          id: variantEdge.node.entityId,
          price: variantEdge.node.prices.price.value * 100,
          requires_shipping: json.data.site.product.type === "Physical",
        }),
      ),
    };
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    const query = `
      query getProductById($productId: Int!) {
        site {
          product(entityId: $productId) {
            id
            entityId
            type
            prices {
              priceRange {
                min {
                  value
                  currencyCode
                }
                max {
                  value
                  currencyCode
                }
              }
              price {
                value
                currencyCode
              }
            }
            variants(first: 100) {
              edges {
                node {
                  entityId
                  prices {
                    price {
                        value
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `;

    // TODO: may need to handle currency, unclear what's presentment vs transaction vs shop - ignoring for MVP, assume its transaction

    const json = await this.graphQLClient.query({
      query,
      variables: { productId: isNaN(Number(handle)) ? handle : Number(handle) },
    });

    return {
      id: json.data.site.product.entityId,
      price_min: json.data.site.product.prices.priceRange.min.value * 100,
      price_max: json.data.site.product.prices.priceRange.max.value * 100,
      variants: json.data.site.product.variants.edges.map(
        (variantEdge: any) => ({
          id: variantEdge.node.entityId,
          price: variantEdge.node.prices.price.value * 100,
          requires_shipping: json.data.site.product.type === "Physical",
        }),
      ),
    };
  }

  async productRecommendations(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsGet(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getStorefrontAccessTokens(accessToken: string): Promise<never> {
    throw new Error("Unsupported");
  }

  async deleteStorefrontAccesstoken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async createStorefrontAccessToken(): Promise<never> {
    throw new Error("Unsupported");
  }

  async productsSearch(): Promise<never> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(data: {
    key: string;
    namespace: string;
  }): Promise<ShopifyShop | undefined> {
    throw new Error("Bigcommerce Unsupported");
  }

  async deleteCartAttribute(data: { key: string }): Promise<void> {
    throw new Error("Bigcommerce Unsupported");
  }

  async getCheckout(): Promise<Checkout | null> {
    const query = `
      query getCheckout {
        site {
          checkout {
            entityId
            subtotal {
              value
              currencyCode
            }
            grandTotal {
              value
              currencyCode
            }
            taxTotal {
              value
              currencyCode
            }
            shippingCostTotal {
              value
              currencyCode
            }
          }
        }
      }
        `;

    const response = await this.graphQLClient.query({ query });
    return response.data.site.checkout;
  }
}
